import React, { useCallback, useMemo, useState } from "react";
import RestaurantPhoto from "./RestaurantPhoto.jsx";
import { MapPin, Globe, Phone, Star } from "lucide-react";
import { postAnalyticsClick } from "../api.js";

/** ---------- utils ---------- */
function fmtMilesTight(meters) {
  if (!Number.isFinite(meters)) return null;
  const miles = meters / 1609.34;
  return `${miles.toFixed(1)}mi`;
}

function clamp(v, min, max) {
  if (!Number.isFinite(v)) return null;
  return Math.min(max, Math.max(min, v));
}

function renderStars(rating05) {
  if (!Number.isFinite(rating05)) return null;
  const clamped = clamp(rating05, 0, 5);
  const filled = Math.round(clamped);
  return (
    <span
      className="rating-stars"
      aria-label={`Rating: ${clamped.toFixed(1)} out of 5`}
      title={`${clamped.toFixed(1)} / 5`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={16}
          style={{ verticalAlign: "-2px" }}
          {...(i < filled
            ? { fill: "currentColor", stroke: "currentColor" }
            : { fill: "none", stroke: "currentColor" })}
        />
      ))}
      <span className="rating-count" style={{ marginLeft: 6 }}>
        {clamped.toFixed(1)}
      </span>
    </span>
  );
}

function mapsUrlFromPlaceId(placeId) {
  return placeId
    ? `https://www.google.com/maps/place/?q=place_id:${placeId}`
    : null;
}

/** ---------- GF Variety ---------- */
function gfColorByLevel(level) {
  switch (level) {
    case 0:
      return { background: "rgba(255, 0, 0, 0.15)", color: "#111827" };
    case 1:
      return { background: "rgba(255,165,0,0.2)", color: "#111827" };
    case 2:
      return { background: "rgba(255,230,0,0.25)", color: "#111827" };
    case 3:
      return { background: "rgba(173,255,47,0.25)", color: "#111827" };
    case 4:
      return { background: "rgba(0,200,83,0.25)", color: "#111827" };
    default:
      return { background: "rgba(0,0,0,0.05)", color: "#111827" };
  }
}

/* Labels per your spec (including 'Penty') */
function gfVarietyText(score) {
  if (!Number.isFinite(score)) return null;
  const s = clamp(Math.round(score), 0, 4);
  const labels = [
    "None",
    "Very Few Options",
    "Small GF Choice",
    "Plenty of GF Options",
    "GF Options Galore",
  ];
  return labels[s];
}
function gfVarietyLabel(score) {
  const label = gfVarietyText(score);
  if (!label) return null;
  return { label, color: gfColorByLevel(clamp(Math.round(score), 0, 4)) };
}

/** ---------- Generic Pill ---------- */
function Pill({ text, color, title, small = false }) {
  if (!text) return null;
  const style = {
    ...(color?.background ? { background: color.background } : {}),
    ...(color?.color ? { color: color.color } : {}),
    ...(small ? { padding: "2px 6px", fontSize: 12, lineHeight: 1.2 } : {}),
    whiteSpace: "nowrap",
  };
  return (
    <span className="pill" title={title || text} style={style}>
      {text}
    </span>
  );
}

/** ---------- Pill color heuristics (by name from data) ---------- */
function pillColorFor(pill, id) {
  if (!id) return { background: "#f3f4f6", color: "#374151" };

  // Use explicit ID logic instead of string matching
  const dangerIds = ["G3"]; // Reported Issues / Glutened
  const cautionIds = ["C3", "G4"]; // Shared Kitchen / Cross-Contamination Mentioned
  const okIds = [
    "M1", "M2", "M4", "M5", "M6", "M7",
    "C1", "C4",
    "G1", "G5"
  ];

  if (dangerIds.includes(id)) {
    return { background: "var(--danger-bg)", color: "var(--danger-fg)" };
  }
  if (cautionIds.includes(id)) {
    return {
      background: "#fff7ed", // orange-50
      color: "#b45309",      // orange-700
      border: "1px solid #fed7aa",
    };
  }
  if (okIds.includes(id)) {
    return { background: "var(--ok-bg)", color: "var(--ok-fg)" };
  }

  // neutral default
  return { background: "#f3f4f6", color: "#374151" };
}


/** ---------- Prioritization (configurable) ---------- */
/* Show at most this many chips on the card */
const MAX_PILLS_SHOWN = 3;

/* Rank by importance to GF diners (lowest index = most important) */
const IMPORTANCE_ORDER = [
  "G5", // Reported Celiac Safe
  "G3", // Reported Issues by GF Diners
  "G4", // Cross-Contamination Mentioned
  "C3", // Shared Kitchen Reported
  "C4", // Dedicated Fryer Reported
  "M1", // Dedicated Gluten-Free Menu
  "C1", // Reported Low Risk
  "M2", // GF Options Labelled
  "M5", // Separate Prep Area Reported
  "M4", // Staff Knowledge Reported
  "M6", // Allergen Policy Reported
  "M7", // Allergen Key Present
  "G2", // Mixed GF Experiences
  "G1", // Reported Safe by GF Diners
  "M3", // GF Ingredients Listed
  "C2", // Reported Moderate Risk
  "D1", "D2", "D3", "D4", // Other dietary options (secondary)
];
const RANK = IMPORTANCE_ORDER.reduce((acc, id, i) => { acc[id] = i; return acc; }, {});

/** Compute eligible (Reported/Present) pills sorted by importance */
function computeEligiblePills(pills) {
  if (!pills || typeof pills !== "object") return [];
  return Object.entries(pills)
    .filter(([id, pill]) => {
      const status = (pill?.status || "").toLowerCase();
      return (status === "reported" || status === "present") && RANK[id] !== undefined;
    })
    .sort(([a], [b]) => (RANK[a] ?? 9999) - (RANK[b] ?? 9999));
}

/** ---------- Safety Pills Row (top N by importance) ---------- */
function SafetyPillsRow({ pills, onSeeAll }) {
  const eligible = computeEligiblePills(pills);
  if (eligible.length === 0) return null;

  const top = eligible.slice(0, MAX_PILLS_SHOWN);
  const hasMore = eligible.length > MAX_PILLS_SHOWN;

  return (
    <div style={{marginTop: 8, marginBottom: 8}}>
      <div className="pill-row pills">
        {top.map(([id, pill]) => {
          const text = pill.name || id;
          const color = pillColorFor(pill, id);

          const confPct =
            typeof pill.confidence === "number" &&
            pill.confidence >= 0 &&
            pill.confidence <= 1
              ? ` – confidence ${(pill.confidence * 100).toFixed(0)}%`
              : "";
          const ev =
            Array.isArray(pill.evidence) && pill.evidence.length > 0
              ? `: ${String(pill.evidence[0]).slice(0, 200)}`
              : "";
          const title = `${text} (${pill.status})${confPct}${ev}`;

          return <Pill key={id} text={text} color={color} title={title} />;
        })}
      </div>

      {hasMore && (
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: "#6b7280", // gray-500
            fontStyle: "italic",
            cursor: "pointer",
          }}
          onClick={onSeeAll}
        >
          Click to see more
        </div>
      )}
    </div>
  );
}

/** ---------- Modal (single column, no headings) ---------- */
function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 12,
      }}
    >
      <div
        className="modal-card panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          maxWidth: 640,
          width: "100%",
          maxHeight: "85vh",
          overflow: "auto",
          borderRadius: 16,
        }}
      >
        {/* Close with “X” top-right */}
        <button
          onClick={onClose}
          aria-label="Close"
          title="Close"
          style={{
            position: "absolute",
            right: 10,
            top: 10,
            border: "1px solid var(--border)",
            borderRadius: 8,
            background: "#f9fafb",
            width: 28,
            height: 28,
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          X
        </button>

        <h3 style={{ marginTop: 0, marginRight: 30 }}>{title}</h3>
        <div>{children}</div>
      </div>
    </div>
  );
}

/** ---------- Pill descriptions for modal ---------- */
const PILL_DESCRIPTIONS = {
  // Menu Clarity
  M1: "The restaurant appears to provide a dedicated gluten-free menu, or a clearly marked GF section.",
  M2: "The menu or reviews reference gluten-free icons, symbols, or explicit GF labels.",
  M3: "Dishes list on menus detail ingredients and/or allergen callouts.",
  M4: "Reviewers report knowledgeable staff who can advise on gluten-free handling.",
  M5: "A distinct prep or cooking area for gluten-free is explicitly mentioned.",
  M6: "The website or menu mentions an allergen-handling policy or staff training.",
  M7: "The menu includes an allergen legend or key explaining icons and abbreviations.",
  // Cross-Contamination
  C1: "Reviews or menus describe strong separation or careful handling suggesting low cross-contact risk.",
  C2: "Only partial precautions or shared spaces are mentioned, indicating moderate risk.",
  C3: "The kitchen is stated as shared or gluten and gluten-free foods are prepared in the same area.",
  C4: "A “dedicated fryer” is explicitly stated.",
  // GF Experience Feedback
  G1: "Gluten-free reviewers report positive experiences and feeling safe.",
  G2: "Gluten-free experiences are mixed or inconsistent across reviews.",
  G3: "Gluten free reviewers have reported getting sick or being “glutened.”",
  G4: "Reviews mention cross-contamination risks (e.g., shared fryers, shared prep).",
  G5: "Reviews consistently describe the restaurant as “celiac safe.”",
  // Other dietary
  D1: "Vegan options available.",
  D2: "Dairy-free options available.",
  D3: "Nut-free options available.",
  D4: "Vegetarian options available.",
};

/** ---------- Card ---------- */
export default function RestaurantCard({ r, analyticsCtx }) {
  const { client_session_id, search_id, plan, meta, position } =
    analyticsCtx || {};

  const [open, setOpen] = useState(false);

  const mapsUrl = useMemo(
    () => r?.maps_url || mapsUrlFromPlaceId(r?.place_id),
    [r]
  );
  const distanceLabel = fmtMilesTight(
    Number.isFinite(r?.distance_m) ? Math.round(r.distance_m) : r?.distance_m
  );

  const logClick = useCallback(
    (action) => {
      if (!client_session_id || !r?.place_id) return;
      const context = {
        zip: meta?.zip ?? null,
        radius_m: meta?.radius ?? null,
        orderby: meta?.orderby ?? null,
        gf_variety: Number.isFinite(r?.gf_variety) ? r.gf_variety : null,
        distance_m: r?.distance_m ?? null,
        plan_hint: plan || undefined,
      };
      postAnalyticsClick({
        client_session_id,
        search_id,
        place_id: r.place_id,
        action,
        position,
        context,
      }).catch(() => {});
    },
    [client_session_id, search_id, plan, meta, r, position]
  );

  const variety = gfVarietyLabel(r?.gf_variety);

  // Make the whole card clickable, but don't steal clicks from inner links
  const openModal = () => setOpen(true);
  const stop = (e) => e.stopPropagation();

  // Precompute eligible pills for “See all” logic and modal rendering
  const eligiblePills = computeEligiblePills(r?.pills);
  const hasMorePills = eligiblePills.length > MAX_PILLS_SHOWN;

  return (
    <>
      <li
        className="card"
        onClick={openModal}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openModal()}
        style={{ cursor: "pointer" }}
      >
        <div className="card-grid" style={{ alignItems: "start" }}>
          {/* Left column */}
          <div className="photo-column">
            <div className="title-wrap" style={{ marginBottom: 8, lineHeight: 1.2 }}>
              {/* Slightly larger title */}
              <h3 className="card-title" style={{ fontSize: 20 }}>
                {r?.name || "Untitled"}
              </h3>

              {/* Cuisine + GF options on the SAME line */}
              {(r?.category || variety) && (
                <div
                  className="category-and-options"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "nowrap", // <-- prevent wrapping
                    marginTop: 2,
                    overflow: "hidden",
                  }}
                >
                  {r?.category && (
                    <span
                      className="primary-type"
                      style={{
                        fontSize: 14,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flexShrink: 1,
                        minWidth: 0, // important for text ellipsis in flex containers
                      }}
                      title={r.category}
                    >
                      {r.category}
                    </span>
                  )}
                  {variety && (
                    <Pill
                      text={variety.label}
                      color={variety.color}
                      title={`GF options: ${variety.label}`}
                      small
                    />
                  )}
                </div>
              )}

              {/* Distance directly UNDER cuisine + variety */}
              {distanceLabel && (
                <div className="primary-type" style={{ marginTop: 2, fontSize: 14 }}>
                  {distanceLabel} away
                </div>
              )}
            </div>

            <RestaurantPhoto
              placeId={r?.place_id}
              photoUrl={r?.photo_url}
              name={r?.name}
            />

            <div className="links" onClick={stop}>
              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  title="Open in Google Maps"
                  aria-label={`Open ${r?.name ?? "restaurant"} in Google Maps`}
                  className="icon-link"
                  onClick={() => logClick("maps")}
                >
                  <MapPin size={18} />
                </a>
              )}
              {r?.website && (
                <a
                  href={r.website}
                  target="_blank"
                  rel="noreferrer noopener"
                  title="Website"
                  aria-label={`Open website for ${r?.name ?? "restaurant"}`}
                  className="icon-link"
                  onClick={() => logClick("website")}
                >
                  <Globe size={18} />
                </a>
              )}
              {r?.phone && (
                <a
                  href={`tel:${r.phone.replace(/\s+/g, "")}`}
                  title="Call"
                  aria-label={`Call ${r?.name ?? "restaurant"}`}
                  className="icon-link"
                  onClick={() => logClick("phone")}
                >
                  <Phone size={18} />
                </a>
              )}
            </div>
          </div>

          {/* Right column */}
          <div className="card-body">
            {/* Slightly more prominent meta line */}
            <div className="meta" style={{ gap: 12, alignItems: "center", display: "flex", fontSize: 15 }}>
              {renderStars(r?.place_rating)}
            </div>

            {r?.formatted_address && <p className="address">{r.formatted_address}</p>}

            {/* Safety signals from pills (top N) + See all */}
            <SafetyPillsRow pills={r?.pills} onSeeAll={openModal} />

            {!!r?.gf_highlights && (
              <p className="notes" style={{ marginTop: 6 }}>
                <strong>Notes:</strong> {r.gf_highlights}
              </p>
            )}
          </div>
        </div>
      </li>

      {/* Modal with eligible pills only, ordered by importance, no headings */}
      <Modal open={open} onClose={() => setOpen(false)} title={r?.name || "Details"}>
        {/* Single-line context (category · variety · distance) */}
        {(r?.category || typeof r?.gf_variety === "number" || distanceLabel) && (
          <div className="primary-type" style={{ fontSize: 13, marginBottom: 8 }}>
            {r?.category}
            {typeof r?.gf_variety === "number" && (
              <>
                {" · "}
                {gfVarietyText(r.gf_variety)}
              </>
            )}
            {distanceLabel && <> · {distanceLabel} away</>}
          </div>
        )}

        {/* Pills + descriptions, importance order, Reported/Present only */}
        <div>
          {eligiblePills.map(([id, pill]) => {
            const color = pillColorFor(pill, id);
            const text = pill.name || id;
            return (
              <div key={id} style={{ marginTop: 15 }}>
                <Pill text={text} color={color} title={`${text} (${pill.status})`} />
                <div className="help" style={{ marginTop: 2 }}>
                  {PILL_DESCRIPTIONS[id] || "Details not available."}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
}
